import React, { useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { UpdateProjectRequest, queryKeys } from 'generated/index';
import { useNavParams } from 'hooks';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queries } from 'RestAPIs/rapini';

import { Alert, Box, Button } from '@mui/material';

import { Autocomplete } from 'components/shared';
import { EditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPage';
import BackToListButton from 'components/shared/CRUD/components/InnerPage/components/BackToListButton';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';

import EditProjectForm from './EditProjectForm';
import ParticipantsGrid from './ParticipantsGrid';
import { addEditProjectSchema } from './add-edit-project-schema';
import EditFooter from 'components/shared/CRUD/components/InnerPage/components/EditFooter';

type Form = InferType<typeof addEditProjectSchema>;

type Option = {
  label: string;
  value: string;
};

type EditProjectPageProps = {
  projectEntityId: string;
};

export default function EditProjectPage({
  projectEntityId
}: EditProjectPageProps) {
  const { t } = useTranslation();
  const methods = useForm<Form>({
    resolver: yupResolver(addEditProjectSchema)
  });
  const { control, formState, handleSubmit, reset } = methods;
  const { fields, append, update, remove } = useFieldArray({
    name: 'participants',
    control
  });

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const { setParams } = useNavParams();

  function onSelected(option) {
    setParams({ projectId: option.value });
  }

  const { data: projects } = queries.useGetProjects(undefined, undefined);
  const project = projects?.find((p) => p.entityId === projectEntityId);

  useEffect(() => {
    if (project) {
      reset({
        name: project.name,
        description: project.description,
        validFrom: dayjs(project.validFrom),
        validTo: dayjs(project.validTo),
        participants:
          project?.participants?.map((p) => ({
            ...p,
            employee: {
              entityId: p.entityId,
              participantEntityId: p.participantEntityId,
              name: p.name
            }
          })) ?? []
      });
    }
  }, [project]);

  const { mutate: updateProject } = mutations.useUpdateProject();
  const queryClient = useQueryClient();

  async function onSubmit(data: Form) {
    const { name, description, validFrom, validTo, participants } = data;

    if (project) {
      const payload: UpdateProjectRequest = {
        entityId: project.entityId,
        name,
        description,
        validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
        validTo: dayjs(validTo).format('YYYY-MM-DD'),
        participants:
          participants?.map((p) => ({
            entityId: p?.entityId ?? null,
            participantEntityId: p.employee.participantEntityId,
            role: p.role,
            validFrom: dayjs(p.validFrom).format('YYYY-MM-DD'),
            validTo: dayjs(p.validTo).format('YYYY-MM-DD')
          })) ?? []
      };

      await updateProject(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getProjects.name);
          showSuccessSnackbar();
        },
        onError: () => {
          showErrorSnackbar();
        }
      });

      setIsEditMode(false);
      return;
    }
  }

  const options: Option[] =
    projects?.map((p) => ({
      label: p?.name ?? '-',
      value: p.entityId as string
    })) ?? [];

  const sortedOptions = options?.sort((a, b) => a.label.localeCompare(b.label));
  const defaultValue = useMemo(
    () => options.find(({ value }) => value === project?.entityId),
    [project]
  );

  function handleCancel() {
    setIsEditMode(false);
    reset();
  }

  return (
    <FormProvider {...methods}>
      <EditPageContext.Provider value={{ isEditable: isEditMode }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={5}
        >
          <Box display="flex" gap={3} alignItems="center">
            {defaultValue && (
              <Box minWidth="180px">
                <Autocomplete
                  variant="outlined"
                  label="Project"
                  idProperty="value"
                  value={defaultValue}
                  options={sortedOptions}
                  onSelected={onSelected}
                  disableClearable
                  disabled={isEditMode}
                  fullWidth
                />
              </Box>
            )}
            <BackToListButton href="/#roster,administrator&page=PROJECTS" />
          </Box>
          {!isEditMode && project?.isEditable && (
            <Button
              variant="contained"
              color="dark"
              disabled={isEditMode}
              onClick={() => setIsEditMode(!isEditMode)}
            >
              {t('edit')}
            </Button>
          )}
          {formState.isDirty && isEditMode && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Alert sx={{ background: 'none', padding: 0 }} severity="warning">
                {t('unsavedData')}
              </Alert>
            </Box>
          )}
        </Box>

        <form>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            pb={8}
            sx={{ width: '100%' }}
          >
            <EditProjectForm control={control} />
            <ParticipantsGrid
              isEditMode={isEditMode}
              participants={fields}
              append={append}
              update={update}
              remove={remove}
              projectValidFrom={project?.validFrom || ''}
              projectValidTo={project?.validTo || ''}
            />
          </Box>

          {isEditMode && (
            <EditFooter
              onCancelClick={handleCancel}
              onClickSave={(e: React.MouseEvent) => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
              submitDisabled={!formState.isDirty}
            />
          )}
        </form>
      </EditPageContext.Provider>
    </FormProvider>
  );
}
